import { useNavigate } from 'react-router-dom';

import { useCurrentUser, useView } from '@/hooks';
import { Button, Icon, Paper, Typography } from '@/ui';

import {
  type PulsePendingDebriefBanner,
  useGetPendingPulseDebriefsQuery,
} from '@/store/api/pulseApi';
import { isCoach } from '@/types/user';
import { formatMonthYear } from '@/utils/pulseReportAdapters';

const toPossessive = (value: string) =>
  value.endsWith('s') ? `${value}'` : `${value}'s`;

const firstName = (fullName: string) =>
  fullName.trim().split(/\s+/u)[0] || fullName;

interface PendingDebriefBannersPureProps {
  banners: PulsePendingDebriefBanner[];
  onReviewBanner?: (banner: PulsePendingDebriefBanner) => void;
}

const PendingDebriefBannersPure = ({
  banners,
  onReviewBanner,
}: PendingDebriefBannersPureProps) => {
  if (banners.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 flex flex-col gap-3">
      {banners.map((banner) => (
        <Paper
          key={banner.reportId}
          className="rounded-xl bg-white p-6"
          elevation={0}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-bloom-500 text-white">
                <Icon>heart_check</Icon>
              </div>
              <div className="flex flex-col gap-1">
                <Typography
                  className="!text-xs !font-medium !leading-4 text-bloom-500"
                  variant="caption"
                >
                  New Insights about your member
                </Typography>
                <Typography className="text-cement-500" variant="subtitle1">
                  {toPossessive(banner.memberName)}{' '}
                  {formatMonthYear(banner.submittedAt)} Pulse Check
                </Typography>
              </div>
            </div>

            <Button
              className="w-full bg-sunny-500 text-cement-600 hover:bg-sunny-600 md:w-auto md:min-w-52"
              variant="contained"
              onClick={() => {
                onReviewBanner?.(banner);
              }}
            >
              View {toPossessive(firstName(banner.memberName))} Responses
            </Button>
          </div>
        </Paper>
      ))}
    </div>
  );
};

const PendingDebriefBanners = () => {
  const navigate = useNavigate();
  const { isClient } = useView();
  const { user } = useCurrentUser();
  const coachUser = isCoach(user);
  const showPendingDebriefBanners = coachUser && !!user.insightsV2Enabled;
  const { data } = useGetPendingPulseDebriefsQuery(undefined, {
    skip: isClient || !showPendingDebriefBanners,
  });

  const banners = data?.banners ?? [];

  if (isClient || !showPendingDebriefBanners || banners.length === 0) {
    return null;
  }

  return (
    <PendingDebriefBannersPure
      banners={banners}
      onReviewBanner={(banner) => {
        navigate(`/coach/members/${banner.memberId}/pulse/${banner.reportId}`);
      }}
    />
  );
};

export default PendingDebriefBanners;
export { PendingDebriefBannersPure };
